/**
 * Editor-local constants: language options, endpointing presets (§6.4),
 * curated starter voices per TTS provider (§6.3), starter prompt templates
 * (§6.2), and the auto knowledge-mode threshold mirrored from §9.2.
 */

import type { TTSProvider } from "../../lib/types";

// ---------------------------------------------------------------------------
// §6.1 language — drives STT model + TTS voice filtering server-side
// ---------------------------------------------------------------------------

export interface LanguageOption {
  value: string;
  label: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "fr-FR", label: "French" },
  { value: "de-DE", label: "German" },
  { value: "it-IT", label: "Italian" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  { value: "nl-NL", label: "Dutch" },
  { value: "ja-JP", label: "Japanese" },
  { value: "ko-KR", label: "Korean" },
  { value: "zh-CN", label: "Chinese (Mandarin)" },
];

// ---------------------------------------------------------------------------
// §6.4 endpointing presets — Snappy / Balanced / Patient → 200/350/600ms
// ADR 0012 measured this wait as pure dead air on top of model latency, and
// ADR 0013 raised Snappy off 100ms. Snappy (200) is still too eager for real
// speech — ordinary pauses of 300-700ms mid-sentence end the turn — so
// Patient is the default and Snappy is for callers who never pause.
// ---------------------------------------------------------------------------

export interface EndpointingPreset {
  id: "snappy" | "balanced" | "patient";
  label: string;
  ms: number;
  help: string;
}

export const ENDPOINTING_PRESETS: EndpointingPreset[] = [
  { id: "snappy", label: "Snappy", ms: 200, help: "Replies fast; may cut off slow talkers." },
  { id: "balanced", label: "Balanced", ms: 350, help: "Good default for most callers." },
  { id: "patient", label: "Patient", ms: 600, help: "Waits longer; better for hesitant speech." },
];

// ---------------------------------------------------------------------------
// §9.2 auto knowledge threshold — mirrors settings.knowledge_inject_token_threshold
// ---------------------------------------------------------------------------

/** At or under this many ready tokens, `auto` injects everything (§9.2). */
export const KNOWLEDGE_INJECT_TOKEN_THRESHOLD = 15_000;

/**
 * Client-side context-budget warning line (§6 validation rules): prompt +
 * restrictions + injected knowledge past this estimate gets a warning banner.
 * 100k leaves headroom for history + responses inside a 128k-context model.
 */
export const CONTEXT_BUDGET_WARN_TOKENS = 100_000;

// ---------------------------------------------------------------------------
// §6.3 curated starter voices — cascaded engine
//
// Voice IDs are provider-specific: Cartesia uses UUIDs from its public
// library, Deepgram uses Aura 2 voice names. The text input beside the list
// accepts any ID from the provider's catalogue.
// ---------------------------------------------------------------------------

export interface StarterVoice {
  id: string;
  name: string;
  style: string;
}

export const STARTER_VOICES: Record<TTSProvider, StarterVoice[]> = {
  cartesia: [
    { id: "694f9389-aac1-45b6-b726-9d9369183238", name: "Sarah", style: "Warm, conversational American female" },
    { id: "79a125e8-cd45-4c13-8a67-188112f4dd22", name: "British Lady", style: "Polished British female" },
    { id: "a0e99841-438c-4a64-b679-ae501e7d6091", name: "Barbershop Man", style: "Friendly, easygoing American male" },
    { id: "156fb8d2-335b-4950-9cb3-a2d33befec77", name: "Helpful Woman", style: "Clear, upbeat support voice" },
    { id: "726d5ae5-055f-4c3d-8355-d9677de68937", name: "Kentucky Man", style: "Relaxed Southern American male" },
  ],
  deepgram: [
    { id: "aura-2-thalia-en", name: "Thalia", style: "Clear, confident American female" },
    { id: "aura-2-asteria-en", name: "Asteria", style: "Bright, engaging American female" },
    { id: "aura-2-apollo-en", name: "Apollo", style: "Composed, natural American male" },
    { id: "aura-2-arcas-en", name: "Arcas", style: "Calm, deep American male" },
    { id: "aura-2-athena-en", name: "Athena", style: "Mature, measured British female" },
  ],
};

/** §6.3 default voice_id: "first in list" for the default provider. */
export function defaultVoiceId(provider: TTSProvider): string {
  return STARTER_VOICES[provider][0].id;
}

// ---------------------------------------------------------------------------
// §6.2 starter prompt templates — support / booking / sales
// ---------------------------------------------------------------------------

export interface PromptTemplate {
  id: "support" | "booking" | "sales";
  label: string;
  prompt: string;
}

export const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: "support",
    label: "Customer support",
    prompt: `You are a friendly customer support agent for {company}. You help callers resolve issues with their orders, accounts, and products.

Guidelines:
- Greet the caller warmly and ask how you can help.
- Ask one question at a time and confirm you understood before moving on.
- Keep answers short and spoken-friendly: one or two sentences, no lists or markdown.
- If the caller's issue needs a human (refund above policy, legal complaint, anything you cannot resolve), offer to take their name and callback number and promise a follow-up.
- Never guess at account details you do not have. Say what you can and cannot see.
- End the call politely once the caller confirms they have what they need.`,
  },
  {
    id: "booking",
    label: "Appointment booking",
    prompt: `You are the booking assistant for {business}. Your only job is to schedule, reschedule, or cancel appointments.

Guidelines:
- Collect, in order: the caller's name, the service they want, and their preferred day and time.
- Offer at most two alternative slots when the preferred time is unavailable.
- Repeat the final booking back to the caller — name, service, day, time — and get an explicit yes before confirming.
- Keep every reply to one or two short sentences. This is a phone call, not an email.
- If the caller asks anything outside booking, politely steer back: you can only help with appointments.
- Confirm the booking, thank the caller, and say goodbye.`,
  },
  {
    id: "sales",
    label: "Sales qualification",
    prompt: `You are a sales assistant for {company}. You qualify inbound interest and route serious prospects to the sales team.

Guidelines:
- Open by asking what prompted the caller's interest.
- Learn, conversationally (never as a checklist): their company, team size, current solution, and rough timeline.
- Answer product questions briefly and honestly. Never invent pricing, features, or availability.
- If they are a fit, offer to book a follow-up call with the sales team and collect their name and number.
- If they are not a fit, thank them honestly and end the call politely.
- Keep replies to one or two sentences — warm, direct, never pushy.`,
  },
];
